import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import type { AppDispatch } from '../../../../store';
import { selectRamp, setSaveError } from '../../../../store/slices/peopleConfigSlice';
import {
  loadRampConfig,
  removeRampOverride,
  saveRamp,
} from '../../../../store/thunks/peopleConfigThunks';
import {
  LocationOnboardingConfig,
  ItemWeeksConfig,
  METRICS_CONFIG,
  MetricConfig,
} from '../types';
import { mwValid, mwDefault } from '../utils';
import { getApiErrorMessage, rampKey } from '../configUtils';
import { useAppLocationNames } from '../useAppLocations';
import { LocationAndTimelineCard } from '../components/LocationAndTimelineCard';
import { OnboardingGlidepathCard } from '../components/OnboardingGlidepathCard';
import { WeeklyTargetGlidepathCard } from '../components/WeeklyTargetGlidepathCard';
import { DropdownSelect } from '../components/DropdownSelect';
import ConfigCard from '../components/ConfigCard';
import OrgDeptPicker from '../components/OrgDeptPicker';
import ConfigConfirmModal from '../components/ConfigConfirmModal';
import TabBody from '../components/TabBody';
import AdminButton from '../../../Admin/components/AdminButton';
import { styles } from '../PeopleHubConfiguration.styles';
import { HeaderActionContext, usePeopleConfig } from '../PeopleConfigContext';

type LoadStatus = 'loading' | 'ready' | 'empty' | 'error';

const clone = (c: LocationOnboardingConfig): LocationOnboardingConfig => JSON.parse(JSON.stringify(c));

/**
 * Onboarding Ramp tab.
 *
 * Every value shown here comes from GET /people/admin/configuration/ramp, cached in Redux
 * (`peopleConfig.ramp`). There is no local default or fallback: until the backend answers the tab
 * shows a skeleton, and if it fails or returns nothing the tab shows an error/empty state.
 * `formState` is the editable copy of the server config for the scope in `formKey`.
 */
const OnboardingRampTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const ctx = usePeopleConfig();
  const { org, dept, user, deptMetrics, status, retry: retryConfig } = ctx;
  const ramp = useSelector(selectRamp);

  // Every location in appData.locations, shared with the Location dropdowns in the other tabs' drawers.
  const locationsList = useAppLocationNames();

  // The "Applies to" selector: 'national' or a location name.
  const [appliesToValue, setAppliesToValue] = useState<string>('national');

  // Keep appliesToValue valid when org/dept/locations change.
  useEffect(() => {
    if (appliesToValue !== 'national' && !locationsList.includes(appliesToValue)) {
      setAppliesToValue('national');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsList]);

  const currentKey = rampKey(org, dept, appliesToValue);
  const published = ramp[currentKey];
  const rampRef = useRef(ramp);
  rampRef.current = ramp;

  // Editable working copy and the scope key it belongs to. Seeded from the Redux cache when this
  // tab is reopened, so switching tabs neither refetches nor shows a skeleton.
  const [formState, setFormState] = useState<LocationOnboardingConfig | null>(() =>
    published ? clone(published) : null
  );
  const [formKey, setFormKey] = useState<string | null>(published ? currentKey : null);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>(published ? 'ready' : 'loading');
  const [reloadTick, setReloadTick] = useState(0);

  const [toast, setToast] = useState<{ message: string; error: boolean } | null>(null);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const showToast = (message: string, error = false) => {
    setToast({ message, error });
    setTimeout(() => setToast((cur) => (cur?.message === message ? null : cur)), 3500);
  };

  // ── Load the ramp for the selected scope ─────────────────────────────────────

  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    const cached = rampRef.current[currentKey];

    if (cached) {
      // Already fetched for this scope: no request. Only reset the form when the scope changed.
      if (formKey !== currentKey) {
        setFormState(clone(cached));
        setFormKey(currentKey);
      }
      setLoadStatus('ready');
      return;
    }

    setLoadStatus('loading');
    dispatch(loadRampConfig(appliesToValue)).then((res) => {
      // A newer scope selection superseded this request: drop the response.
      if (id !== requestId.current || res.outcome === 'stale') return;
      if (res.outcome === 'ready') {
        setFormState(clone(res.config));
        setFormKey(currentKey);
        setLoadStatus('ready');
      } else {
        // Unsaved edits for this same scope (if any) stay on screen; the error is shown inline.
        setLoadStatus(res.outcome);
      }
    });
    // formKey is read only to decide whether to reset the form; it must not retrigger the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, reloadTick, dispatch]);

  const retryLoad = () => setReloadTick((t) => t + 1);

  // `status` is the shared load of org/department metadata, locations and every other tab. Like
  // the other tabs, nothing is shown as loaded until it is 'ready'. The /ramp request itself
  // starts straight away (in parallel), so the form is usually ready the moment metadata is.
  const configReady = status === 'ready';
  const formReady = configReady && !!formState && !!published && formKey === currentKey;

  // ── Derived state ─────────────────────────────────────────────────────────────

  const hasChanges = useMemo(
    () => (formReady ? JSON.stringify(formState) !== JSON.stringify(published) : false),
    [formReady, formState, published]
  );

  const isMilestoneSequenceValid = formState
    ? mwValid(formState.milestoneWeeks, formState.onboardingWeeks)
    : false;

  const canSave = formReady && hasChanges && isMilestoneSequenceValid;

  const deptKey = `${org}:${dept}`;
  const deptMetricIds: string[] = deptMetrics[deptKey] ?? METRICS_CONFIG.map((m) => m.key);
  const activeMetrics: MetricConfig[] = METRICS_CONFIG.filter((m) => deptMetricIds.includes(m.key));

  const rampScope = appliesToValue === 'national' ? `${org} ${dept}` : `${appliesToValue} / ${dept}`;

  // A location only has its own ramp when the backend says LOCATION_OVERRIDE; INHERITED means it
  // is showing the national ramp.
  const hasOwnOverride = appliesToValue !== 'national' && published?.scopeType === 'LOCATION_OVERRIDE';
  const isInherited = appliesToValue !== 'national' && !hasOwnOverride;

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const update = (fn: (prev: LocationOnboardingConfig) => LocationOnboardingConfig) =>
    setFormState((prev) => (prev ? fn(prev) : prev));

  const handleTimelineChange = (newWeeks: number) => {
    const clamped = Math.min(26, Math.max(4, newWeeks));
    update((prev) => {
      const clampedMilestones = prev.milestoneWeeks.map((m) => Math.min(m, clamped)) as [
        number,
        number,
        number
      ];
      const validMilestones = mwValid(clampedMilestones, clamped)
        ? clampedMilestones
        : mwDefault(clamped);

      const newTargets: Record<string, number[]> = {};
      METRICS_CONFIG.forEach(({ key }) => {
        const existing = prev.targets[key] || [];
        const lastVal = existing[existing.length - 1] ?? 0;
        newTargets[key] = Array.from({ length: clamped }, (_, idx) => existing[idx] ?? lastVal);
      });

      return {
        ...prev,
        onboardingWeeks: clamped,
        milestoneWeeks: validMilestones,
        itemWeeks: {
          preferred: Math.min(clamped, prev.itemWeeks.preferred),
          training: Math.min(clamped, prev.itemWeeks.training),
          certification: Math.min(clamped, prev.itemWeeks.certification),
        },
        targets: newTargets,
      };
    });
  };

  const handleMilestoneWeekChange = (index: 0 | 1 | 2, week: number) => {
    update((prev) => {
      const nextMilestones = [...prev.milestoneWeeks] as [number, number, number];
      nextMilestones[index] = week;
      return { ...prev, milestoneWeeks: nextMilestones };
    });
  };

  const handleItemWeekChange = (key: keyof ItemWeeksConfig, week: number) => {
    update((prev) => ({ ...prev, itemWeeks: { ...prev.itemWeeks, [key]: week } }));
  };

  const handleTargetCellChange = (metricKey: string, weekIndex: number, val: string) => {
    const num = parseFloat(val);
    update((prev) => {
      const existing = prev.targets[metricKey] || [];
      const updated = existing.map((cur, idx) =>
        idx === weekIndex ? (val === '' ? 0 : isNaN(num) ? cur : num) : cur
      );
      return { ...prev, targets: { ...prev.targets, [metricKey]: updated } };
    });
  };

  const handleReset = () => {
    if (!published) return;
    setFormState(clone(published));
    showToast('Changes reset to published configuration.');
  };

  // ── Save: PUT /ramp. The UI changes only after the backend confirms. ─────────

  const handleSave = () => {
    if (!canSave || !formState || saveLoading) return;
    ctx.guard(async () => {
      setSaveLoading(true);
      try {
        const saved = await dispatch(saveRamp(appliesToValue, formState, user));
        setFormState(clone(saved));
        setFormKey(currentKey);
        showToast(`Published People Hub targets for ${rampScope}.`);
      } catch (err) {
        console.error('[OnboardingRampTab] Save failed', err);
        // formState is untouched, so the user's edits remain and they can try again.
        const message = getApiErrorMessage(err);
        dispatch(setSaveError(message));
        showToast(message, true);
      } finally {
        setSaveLoading(false);
      }
    });
  };

  // ── Remove location override: DELETE /ramp, then reload the inherited national ramp ───

  const handleRemoveOverride = async () => {
    setRemoveOpen(false);
    try {
      await dispatch(removeRampOverride(appliesToValue));
    } catch (err) {
      console.error('[OnboardingRampTab] Remove override failed', err);
      showToast(getApiErrorMessage(err, 'Error removing override. Please try again.'), true);
      return;
    }
    const res = await dispatch(loadRampConfig(appliesToValue, true));
    if (res.outcome === 'ready') {
      setFormState(clone(res.config));
      setFormKey(currentKey);
      showToast(`${appliesToValue} will now use the ${org} ${dept} national ramp.`);
    } else {
      // The override is gone but the inherited ramp could not be read; show the retry state.
      setFormKey(null);
      setLoadStatus(res.outcome === 'stale' ? 'loading' : res.outcome);
    }
  };

  // ── Header "Save Configuration" button (lives in the page shell) ──────────────

  const setHeaderAction = useContext(HeaderActionContext);
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setHeaderAction(
      <TouchableOpacity
        style={[styles.btnPrimary, !canSave && styles.btnPrimaryDisabled]}
        onPress={() => handleSaveRef.current()}
        disabled={!canSave || saveLoading}
        activeOpacity={0.8}
      >
        <Text style={styles.btnPrimaryText}>{saveLoading ? 'Saving…' : 'Save Configuration'}</Text>
      </TouchableOpacity>
    );
  }, [canSave, saveLoading, setHeaderAction]);

  useEffect(() => () => setHeaderAction(null), [setHeaderAction]);

  // ── Render ────────────────────────────────────────────────────────────────────

  const infoBanner = (
    <View style={styles.infoBanner}>
      <MaterialIcons
        name="lightbulb-outline"
        size={20}
        color="#0265B5"
        style={styles.infoBannerIcon}
      />
      <Text style={styles.infoBannerText}>
        Changes apply to new and active onboarding glidepaths for the selected org, department, and
        location. Pulse Checks compare each new hire against the target for their current onboarding
        week.
      </Text>
      {loadStatus === 'loading' && formReady && (
        <ActivityIndicator size="small" color="#0265B5" style={{ marginLeft: 8 }} />
      )}
    </View>
  );

  // Not ready to show: skeleton while the shared config or this scope's ramp is loading, error +
  // Retry if either failed, or an empty message. The scope selector stays available.
  if (!formReady) {
    const scopeOptions = [
      { value: 'national', label: 'National (every location)' },
      ...locationsList.map((loc) => ({ value: loc, label: loc })),
    ];
    // Skeleton unless there is a definite outcome. 'ready' (card body) is used only for the
    // 'empty' outcome; a stale loadStatus right after a scope change therefore still shows the skeleton.
    const empty = status === 'ready' && loadStatus === 'empty';
    const cardStatus =
      status === 'error' || (status === 'ready' && loadStatus === 'error')
        ? 'error'
        : empty
          ? 'ready'
          : 'loading';
    return (
      <TabBody>
        <OrgDeptPicker />
        {infoBanner}
        <ConfigCard
          title="Location and Timeline"
          status={cardStatus}
          onRetry={status === 'error' ? retryConfig : retryLoad}
          action={
            <View style={{ minWidth: 240 }}>
              <DropdownSelect value={appliesToValue} options={scopeOptions} onChange={setAppliesToValue} />
            </View>
          }
        >
          {/* Shown only when the card status is 'ready', which here means the 'empty' outcome. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <Text style={styles.formHint}>
              {empty ? `No onboarding ramp is configured for ${rampScope}.` : ''}
            </Text>
            <AdminButton label="Reload" variant="secondary" small onPress={retryLoad} />
          </View>
        </ConfigCard>
      </TabBody>
    );
  }

  return (
    <View>
      <View style={{ gap: 20 }}>
        {/* Org / Department picker (shared with other tabs) */}
        <OrgDeptPicker />

        {infoBanner}

        {/* Reload failed (for example after Retry) but the user's edits are still shown. */}
        {loadStatus === 'error' ? (
          <View style={styles.infoBanner}>
            <Text style={styles.infoBannerText}>
              The latest configuration could not be loaded. Your unsaved changes are kept.
            </Text>
            <AdminButton label="Retry" variant="secondary" small onPress={retryLoad} />
          </View>
        ) : null}

        {/* Card 1: Location and Timeline */}
        <LocationAndTimelineCard
          org={org}
          dept={dept}
          appliesToValue={appliesToValue}
          locationsList={locationsList}
          hasOverride={hasOwnOverride}
          onAppliesToChange={setAppliesToValue}
          onRemoveOverride={() => setRemoveOpen(true)}
          onboardingWeeks={formState!.onboardingWeeks}
          onTimelineChange={handleTimelineChange}
          publishedConfig={published}
          isInherited={isInherited}
        />

        {/* Card 2: Onboarding Glidepath */}
        <OnboardingGlidepathCard
          onboardingWeeks={formState!.onboardingWeeks}
          milestoneWeeks={formState!.milestoneWeeks}
          onMilestoneWeekChange={handleMilestoneWeekChange}
          itemWeeks={formState!.itemWeeks}
          onItemWeekChange={handleItemWeekChange}
        />

        {/* Card 3: Weekly Pulse Check Target Glidepath */}
        <WeeklyTargetGlidepathCard
          onboardingWeeks={formState!.onboardingWeeks}
          milestoneWeeks={formState!.milestoneWeeks}
          targets={formState!.targets}
          onTargetCellChange={handleTargetCellChange}
          onReset={handleReset}
          onSave={handleSave}
          hasChanges={hasChanges}
          canSave={canSave}
          saveLoading={saveLoading}
          metrics={activeMetrics}
        />
      </View>

      {/* Remove Override confirmation modal */}
      {removeOpen && (
        <ConfigConfirmModal
          title="Remove location override"
          message={`${appliesToValue} will use the ${org} ${dept} national ramp. Changes apply to new and active onboarding glidepaths at ${appliesToValue}.`}
          cancelLabel="Cancel"
          confirmLabel="Remove Override"
          onCancel={() => setRemoveOpen(false)}
          onConfirm={handleRemoveOverride}
        />
      )}

      {/* Floating toast notification */}
      {toast && (
        <View style={styles.toastContainer}>
          <MaterialIcons
            name={toast.error ? 'error' : 'check-circle'}
            size={18}
            color={toast.error ? '#EF4444' : '#22C55E'}
          />
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}
    </View>
  );
};

export default OnboardingRampTab;
